import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AdminMetricCard from './components/AdminMetricCard';
import RevenueAnalyticsChart from './components/RevenueAnalyticsChart';
import QuickActionCard from './components/QuickActionCard';
import ActivityFeed from './components/ActivityFeed';
import SystemHealthPanel from './components/SystemHealthPanel';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState('month');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    navigate('/super-admin-login');
  };

  const overviewMetrics = [
    {
      title: 'Total Clients',
      value: 1247,
      trend: 'up',
      trendValue: 12.5,
      icon: 'Users',
      color: 'blue',
      description: 'Registered companies'
    },
    {
      title: 'Active Subscriptions',
      value: 892,
      trend: 'up',
      trendValue: 8.3,
      icon: 'CheckCircle',
      color: 'emerald',
      description: 'Currently active plans'
    },
    {
      title: 'Monthly Recurring Revenue',
      value: '₹18.4L',
      trend: 'up',
      trendValue: 15.7,
      icon: 'DollarSign',
      color: 'violet',
      description: 'MRR this month'
    },
    {
      title: 'Plan Distribution',
      value: '42% Growth',
      trend: 'neutral',
      trendValue: 0,
      icon: 'PieChart',
      color: 'amber',
      description: 'Most popular plan'
    }
  ];

  const quickActions = [
    {
      title: 'Client Management',
      description: 'View and manage all registered companies',
      icon: 'Users',
      color: 'blue',
      count: 1247,
      action: () => console.log('Navigate to Client Management')
    },
    {
      title: 'Subscription Management',
      description: 'Manage plans, pricing, and features',
      icon: 'CreditCard',
      color: 'violet',
      count: 892,
      action: () => console.log('Navigate to Subscription Management')
    },
    {
      title: 'Payment Management',
      description: 'Track transactions and process refunds',
      icon: 'Wallet',
      color: 'emerald',
      count: 3421,
      action: () => console.log('Navigate to Payment Management')
    },
    {
      title: 'Content Management',
      description: 'Manage website content and blog posts',
      icon: 'FileText',
      color: 'amber',
      count: 156,
      action: () => console.log('Navigate to Content Management')
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Admin Sidebar */}
      <AdminSidebar isCollapsed={sidebarCollapsed} />

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Admin Header */}
        <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-30 elevation-md">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="hidden lg:flex items-center justify-center w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-smooth"
                  aria-label="Toggle sidebar"
                >
                  <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} color="#F59E0B" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/50">
                    <Icon name="Shield" size={20} color="#F59E0B" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-white font-heading">
                      Super Admin Dashboard
                    </h1>
                    <p className="text-xs text-slate-400 font-caption">
                      Business Jadhak Platform Management
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 px-4 py-2 bg-slate-800 rounded-lg border border-slate-700">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Icon name="User" size={16} color="#F59E0B" />
                    </div>
                    <div className="hidden sm:block">
                      <p className="text-sm font-medium text-white">Super Admin</p>
                      <p className="text-xs text-slate-400">admin@businessjadhak.com</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="default"
                    iconName="LogOut"
                    onClick={handleLogout}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-6 py-8">
          {/* Overview Metrics */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 font-heading">
                  Platform Overview
                </h2>
                <p className="text-sm text-slate-600 font-body mt-1">
                  Real-time metrics and performance indicators
                </p>
              </div>
              <div className="flex items-center gap-2">
                {['day', 'week', 'month', 'year']?.map((period) => (
                  <button
                    key={period}
                    onClick={() => setTimePeriod(period)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      timePeriod === period
                        ? 'bg-slate-900 text-white elevation-sm' :'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    {period?.charAt(0)?.toUpperCase() + period?.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {overviewMetrics?.map((metric, index) => (
                <AdminMetricCard key={index} {...metric} />
              ))}
            </div>
          </div>

          {/* Revenue Analytics & System Health */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <RevenueAnalyticsChart timePeriod={timePeriod} />
            </div>
            <div>
              <SystemHealthPanel />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 font-heading mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickActions?.map((action, index) => (
                <QuickActionCard key={index} {...action} />
              ))}
            </div>
          </div>

          {/* Activity Feed */}
          <ActivityFeed />
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;